// Minimal RFC-4180-ish CSV parser. Kept hand-rolled to avoid a 50kb
// papaparse dep in the shared package — the wizard UI uses papaparse
// in the browser for performance, but commit-time validation runs in
// the Edge Function (Deno) where we want zero non-stdlib dependencies.
//
// Handles: quoted fields, embedded commas, embedded newlines in quotes,
// CRLF and LF, escaped quotes ("").
//
// Does NOT handle: arbitrary delimiters (always `,`), windows-1252 (we
// require UTF-8 input — converted in the wizard upload step).

export interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
}

const enum State {
  Field,
  QuotedField,
  QuoteInQuoted,
}

export function parseCsv(input: string): ParsedCsv {
  // Strip:
  //   - UTF-8 BOM (Excel emits this on every CSV export)
  //   - embedded null bytes (occasional Windows-clipboard artifact)
  // Then normalize line endings to \n.
  const text = input
    .replace(/^\uFEFF/, '')
    .replace(/\0/g, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');

  const rawRows: string[][] = [];
  let state: State = State.Field;
  let field = '';
  let row: string[] = [];

  for (const ch of text) {
    if (state === State.Field) {
      if (ch === ',') {
        row.push(field);
        field = '';
      } else if (ch === '\n') {
        row.push(field);
        rawRows.push(row);
        row = [];
        field = '';
      } else if (ch === '"' && field === '') {
        state = State.QuotedField;
      } else {
        field += ch;
      }
    } else if (state === State.QuotedField) {
      if (ch === '"') {
        state = State.QuoteInQuoted;
      } else {
        field += ch;
      }
    } else {
      // QuoteInQuoted — either escaped quote, or end of quoted field.
      if (ch === '"') {
        field += '"';
        state = State.QuotedField;
      } else if (ch === ',') {
        row.push(field);
        field = '';
        state = State.Field;
      } else if (ch === '\n') {
        row.push(field);
        rawRows.push(row);
        row = [];
        field = '';
        state = State.Field;
      } else {
        // Stray content after a closing quote — treat as raw and continue.
        field += ch;
        state = State.Field;
      }
    }
  }

  // Flush trailing field if file did not end with newline.
  if (field !== '' || row.length > 0) {
    row.push(field);
    rawRows.push(row);
  }

  if (rawRows.length === 0) {
    return { headers: [], rows: [] };
  }

  const headers = (rawRows[0] ?? []).map(normalizeHeader);
  const dataRows = rawRows.slice(1).filter((r) => r.some((cell) => cell.trim() !== ''));
  const rows = dataRows.map((cells) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      obj[h] = (cells[idx] ?? '').trim();
    });
    return obj;
  });

  return { headers, rows };
}

export function normalizeHeader(s: string): string {
  // Strip non-alphanumerics FIRST so a trailing emoji/symbol doesn't leave
  // its separating space behind as a dangling underscore. Then collapse
  // whitespace runs into single underscores.
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s_]/g, '')
    .trim()
    .replace(/\s+/g, '_');
}
